import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { CommonModule, KeyValuePipe } from '@angular/common';
import { BaseVariableService, VariableCrudManagerService } from '@wms/variables';
import { FormsModule } from '@angular/forms';
import {BaseComponent, WMSEditorComponent, WmsMessageComponent, WmsTypeaheadComponent} from '@wms/components';
import {
  EventHandlerComponent
} from '@wms/app/properties-panel/properties-template/components/event-handler/event-handler.component';
import { each, forEach, isNumber, isObject, isString } from 'lodash-es';
import { fromJson, toJson } from '@wms/core';
import { getValidJSON, runtimeEventManager } from '@wm/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { CommonWidgetDirective } from "@wms/directives";

@Component({
  standalone: true,
  selector: 'wms-variables-template',
  templateUrl: './variables-template.component.html',
  styleUrls: ['./variables-template.component.less'],
  imports: [KeyValuePipe, CommonModule, FormsModule, WmsTypeaheadComponent, EventHandlerComponent, WMSEditorComponent, WmsMessageComponent,
  CommonWidgetDirective]
})
export class VariablesTemplateComponent extends BaseComponent implements OnDestroy {
  @Input() templateType: string;
  @Input() selectedService: any;
  @Input() selectedVariable: any;
  @Input() propertyName: any;
  @Input() editable:boolean;
  @Output() onModelChange = new EventEmitter<any>();
  @ViewChild('varpropbooleaninputfirst', { static: true }) varpropbooleaninputfirstTemplate: TemplateRef<any> | undefined;
  @ViewChild('varpropstring', { static: true }) varpropstringTemplate: TemplateRef<any> | undefined;
  @ViewChild('varpropnumber', { static: true }) varpropnumberTemplate: TemplateRef<any> | undefined;
  @ViewChild('varpropboolean', { static: true }) varpropbooleanTemplate: TemplateRef<any> | undefined;
  @ViewChild('varpropdate', { static: true }) varpropdateTemplate: TemplateRef<any> | undefined;
  @ViewChild('varpropdatetime', { static: true }) varpropdatetimeTemplate: TemplateRef<any> | undefined;
  @ViewChild('varproplist', { static: true }) varproplistTemplate: TemplateRef<any> | undefined;
  @ViewChild('varpropevent', { static: true }) varpropeventTemplate: TemplateRef<any> | undefined;
  @ViewChild('varproporderby', { static: true }) varproporderbyTemplate: TemplateRef<any> | undefined;
  @ViewChild('varproptypeahead', { static: true }) varproptypeaheadTemplate: TemplateRef<any> | undefined;
  @ViewChild('varpropjson', { static: true }) varpropjsonTemplate: TemplateRef<any> | undefined;
  @ViewChild('varpropradioset', { static: true }) varpropradiosetTemplate: TemplateRef<any> | undefined
  @ViewChild("codeEditor") wmsEditorEl: WMSEditorComponent;

  booleanFieldOptions: any = {
    '': undefined,
    //'null': 'null', // will be included after clarity from back-end
    'true': true,
    'false': false
  }

  dateChangeSubscription: Subscription;

  constructor(public baseVariableService: BaseVariableService, public varManagerService: VariableCrudManagerService, private router: Router) {
    super();
    this.dateChangeSubscription = this.baseVariableService.onDateChangeObs().subscribe(() => {
      this.onDataChange();
    });
  }

  ngOnInit(): void {
  }

  getTemplate(): any {
    switch (this.templateType) {
      case 'var-prop-boolean-inputfirst':
        return this.varpropbooleaninputfirstTemplate;
      case 'var-prop-string':
        return this.varpropstringTemplate;
      case 'var-prop-number':
        return this.varpropnumberTemplate;
      case 'var-prop-boolean':
        return this.varpropbooleanTemplate;
      case 'var-prop-date':
        return this.varpropdateTemplate;
      case 'var-prop-datetime':
        return this.varpropdatetimeTemplate;
      case 'var-prop-list':
        return this.varproplistTemplate;
      case 'var-prop-event':
        return this.varpropeventTemplate;
      case 'var-prop-order-by':
        return this.varproporderbyTemplate;
      case 'var-prop-typeahead':
        return this.varproptypeaheadTemplate;
      case 'var-prop-json':
        return this.varpropjsonTemplate;
      case 'var-prop-radioset':
        return this.varpropradiosetTemplate;
    }
  }

  refreshFieldEditor() {
    this.baseVariableService.jsonEditorViewOption = 1;
    let textEditorValue: any = this.baseVariableService.jsonTextEditor.value,
    validJson: any = getValidJSON(textEditorValue);
    if(validJson){
      this.baseVariableService.selectedVariable.variableObject['dataSet'] = validJson;
    }
  }

  refreshTextEditor() {
    this.baseVariableService.jsonEditorViewOption = 2;
    this.baseVariableService.jsonTextEditor = { ...this.baseVariableService.jsonTextEditor, value: JSON.stringify(this.baseVariableService.selectedVariable.variableObject['dataSet'], undefined, 2), language: "json" };
  }

  /* function to add a field in "list type" dataset of current variable */
  addField() {
    let variable: any = this.baseVariableService.selectedVariable.variableObject,
      newField: any = {};

    /* take all the keys from first item of dataSet, and construct the new field */
    Object.keys(variable.dataSet[0]).forEach((key: any) => {
      newField[key] = variable.type === "entry" ? key + variable.dataSet.length : "";
    });

    variable.dataSet.push(newField);
    this.onDataChange(variable.dataSet);
  }

  /* function to remove selected field */
  removeField(index: any) {
    let dataset = this.selectedVariable.variableObject.dataSet;
    /* remove the field only if more than one fields are present in dataset */
    if (dataset.length > 1) {
      dataset.splice(index, 1);
      this.onDataChange(dataset);
    }
  };

  trackByFn(index: any, item: any) {
    return index;
  }

  /**
   * This method loads the default view for primitive type static variable,
   * as they don't have an editor.
   * @param $s, scope
   */
  defaultViewForPrimitiveType() {
    this.baseVariableService.jsonEditorViewOption = 1;
  }

  /**
   * This function returns the value by converting it to selected type
   * @param dataValue, value to be type checked
   * @returns {*}
   */
  datavalueTypeConversion(dataValue: any) {
    let variable,
      dateObj: any;
    if (dataValue !== undefined) {
      variable = this.selectedVariable.variableObject;
      /*manipulating dataValue based on the type of the static variable, if provided value not matching
       * datatypes.*/
      switch (variable.type.toLowerCase()) {
        case 'date':
          this.defaultViewForPrimitiveType();
          dateObj = new Date(dataValue);
          /*checking for invalid date assignment*/
          if (dateObj != "Invalid Date") {
            dataValue = dateObj.valueOf();
          } else {
            dataValue = new Date().valueOf();
          }
          break;
        case 'boolean':
          this.defaultViewForPrimitiveType();
          /*checking if values are anything other than true or false*/
          if (dataValue === true || dataValue === "true") {
            dataValue = true;
          } else {
            dataValue = false;
          }
          break;
        case 'number':
          this.defaultViewForPrimitiveType();
          /*checking if values assigned are a number or not*/
          if (!isNumber(dataValue)) {
            dataValue = Number(dataValue);
            if (!dataValue) {
              dataValue = 0;
            }
          }
          break;
        case 'string':
          this.defaultViewForPrimitiveType();
          /*checking if values assigned are of string type or not*/
          if (!isString(dataValue)) {
            dataValue = String(dataValue);
          }
          break;
      }
      return dataValue
    }
  }

  updateEditorContent(dataset: any): any {
    this.baseVariableService.jsonTextEditor.value = dataset.value;
    try {
      const editorValue = JSON.parse(dataset.value);
      this.baseVariableService.selectedVariable.variableObject['dataSet'] = editorValue;
      this.baseVariableService.selectedVariable.variableObject.isList = editorValue.length > 0
      this.baseVariableService.markUpdated();
      this.baseVariableService.disableDialogActions = false;
    } catch (e) {
      return false;
    }
  }

  /* function invoked on dataset change for current variable */
  onDataChange(dataSet?: any) {
    let variable: any = this.selectedVariable.variableObject;
    if (!dataSet) {
      dataSet = this.selectedVariable.variableObject.dataSet;
    }
    let firstKey = Object.keys(dataSet)[0],
      firstValue = dataSet[firstKey],
      content,
      firstValueInListItem: any;

    if (variable.isList) {
      forEach(dataSet, (val: any) => {
        // For each object present inList, convert the first value of the object
        // to the variable type.
        if (isObject(val)) {
          let keys: any = Object.keys(val);
          firstValueInListItem = (val as any)[keys[0]];
          (val as any)[Object.keys(val)[0]] = this.datavalueTypeConversion(firstValueInListItem);
        }
      });
    } else {
      dataSet[firstKey] = this.datavalueTypeConversion(firstValue);
    }
    content = toJson(dataSet, true);
    dataSet = fromJson(content);
    /* JSON.stringify is not used as ng-repeat appends a '$$hashkey' field to the dataset fields */

    /* update editor content */
    // $scope.activeWorkSpace.editorContent = dataSet ? content : '';

    if (this.selectedVariable.category === "wm.Variable") {
      /* if change triggered by new variable selection from list and not by actual dataset change, return */
      if (this.varManagerService.variableChanged) {
        this.varManagerService.variableChanged = undefined;
      }

      /* update the new bindings. If fields are modified in the dataSet(created, updated, deleted), they should be reflected in the bindings */
      this.selectedService.__updateBindingInfo();
    }
    if (this.selectedVariable.category !== "wm.LoginVariable") {
      this.baseVariableService.markUpdated();
    }
  };

  getColSpan(data: any) {
    return data.isInvalid ? 2 : undefined
  }

  showModelKey() {
    const modelKey: any = this.selectedVariable.propertyObj[this.propertyName]?.modelObj[this.selectedVariable.propertyObj[this.propertyName].modelKey];
    return (modelKey && modelKey.toString().indexOf('bind:') !== 0) || modelKey === '' || !modelKey;
  }

  alterOrderBy(propertyObj: any, data: any) {
    let modelObj = propertyObj.modelObj,
      modelVal = modelObj[propertyObj.modelKey],
      newOrder = data.order === 'desc' ? 'asc' : 'desc';
    modelObj[propertyObj.modelKey] = modelVal.replace(data.key + ' ' + data.order, data.key + ' ' + newOrder);
    data.order = newOrder;
  }

  deleteFieldOrderBy(propertyObj: any, data: any) {
    let modelObj = propertyObj.modelObj,
      modelVal = modelObj[propertyObj.modelKey],
      propLength = propertyObj.value.length,
      replaceStr = data.key + ' ' + data.order;
    //replace string accordingly to the index
    if (data.index === 0) {
      replaceStr = propLength === 1 ? replaceStr : replaceStr + ',';
    } else {
      replaceStr = ',' + replaceStr;
    }
    //push the removed field into the typeahead object
    propertyObj.options[data.key] = data.key;
    modelObj[propertyObj.modelKey] = modelVal.replace(replaceStr, '');
    each(propertyObj.value, ((field: any, index: any): any => {
      if (field.index === data.index) {
        //splice the removed from the
        propertyObj.value.splice(index, 1);
        return false;
      }
    }));
  }

  addOrderByField(propertyObj: any) {
    if (!propertyObj.typeaheadvalue) {
      return;
    }
    let modelObj = propertyObj.modelObj,
      modelVal = modelObj[propertyObj.modelKey],
      oldWdgtVal = propertyObj.typeaheadvalue,
      newVal = propertyObj.typeaheadvalue + ' ' + propertyObj.order,
      updatedVal = modelVal ? modelVal + ',' + newVal : newVal;
    //update the model value
    modelObj[propertyObj.modelKey] = updatedVal;
    //update the orderBy Widget value
    propertyObj.value.push({
      'key': propertyObj.typeaheadvalue,
      'order': propertyObj.order,
      'index': propertyObj.value.length
    });
    //reset the typeahead model value
    propertyObj.typeaheadvalue = '';
    //remove the value from typeahead list options
    delete propertyObj.options[oldWdgtVal];
  }

  navigateToScript($event: any) {
    if (this.selectedVariable.variableObject.owner === 'App') {
      this.router.navigate(['/files', {
        name: 'app.js',
        relativePath: '/src/main/webapp/app.js',
        path: '../project/src/main/webapp/app.js',
        type: "file"
      }]).then(()=> {
         runtimeEventManager.emit('navigateToFunction', $event);
      });
    } else {
      runtimeEventManager.emit('navigateToScript', $event);
    }
    this.varManagerService.saveAndClose(true, true, true);
  }

  skipSorting() {
    return 0;
  }

  override ngOnDestroy(): void {
    if (this.dateChangeSubscription) {
      this.dateChangeSubscription.unsubscribe();
    }
  }

  isModelVarTypeProp() {
    return this.selectedVariable.category === 'wm.Variable' && this.propertyName === 'type';
  }

  handleModelChange(evt: any) {
    if (this.isModelVarTypeProp()) {
      this.selectedVariable.propertyObj[this.propertyName].modelObj[this.selectedVariable.propertyObj[this.propertyName].modelKey] = evt;
    }
    this.onModelChange.emit({propertyName: this.propertyName, $event: evt, selectedService: this.selectedService});
  }

  clearModel() {
    this.selectedVariable.propertyObj[this.propertyName].modelObj[this.selectedVariable.propertyObj[this.propertyName].modelKey] = '';
  }

  getOptionModel = () => {
    if (this.isModelVarTypeProp() && Array.isArray(this.selectedVariable.propertyObj[this.propertyName].options)) {
      return this.selectedVariable.propertyObj[this.propertyName].options?.find((op: any) => op.value === this.selectedVariable.propertyObj[this.propertyName].modelObj[this.selectedVariable.propertyObj[this.propertyName].modelKey])?.name
    } else {
      return this.selectedVariable.propertyObj[this.propertyName].modelObj[this.selectedVariable.propertyObj[this.propertyName].modelKey]
    }
  }
}
